/**
 * LiveSectionPreview — Iter 189c (Phase A) — drift-free template previews.
 * ============================================================================
 *
 * Renders a template's actual section renderer at a fixed 1200px width inside
 * a scaled wrapper. Because we call the *same* block renderer that runs on
 * insertion (`HeroBlock`, `PremiumSectionRenderer` families, …), the preview
 * is guaranteed to match the inserted section — zero drift by construction.
 *
 * Supported block types (Phase A):
 *   • hero          — /blocks/HeroBlock.js  (all 14 layouts: centered, floating-card,
 *                                            overlapping, collage, large-typography,
 *                                            offset-asymmetric, brand-showcase,
 *                                            video-hero, multi-image, layered,
 *                                            minimal, split-image-left, split-image-right,
 *                                            hero-fullbleed, …)
 *   • editorial     — /premium/PremiumSectionRenderer (pull-quote, two-column-manifesto,
 *                                                      centered-statement)
 *   • features / split / stats / testimonials / team / services / cta / timeline /
 *     events / logos / directory  — via renderPremiumSection()
 *
 * Contract:
 *   • `tpl.blocks[0]` = { type, layout, content }
 *   • We dispatch off `type` to the correct real renderer.
 *   • ErrorBoundary catches any render crash → returns `null` → parent falls
 *     back to the drawn miniature (RichTilePreview).
 *
 * Sizing: outer container = w-full h-full (fills the panel). Inner scaler
 * = fixed 1200px width scaled to fit via `transform: scale(...)`.
 */
import React, { useEffect, useRef, useState } from 'react';
import HeroBlock from '../../blocks/HeroBlock';
import {
  AboutBlock, TestimonialBlock, MarqueeBlock, VideoBlock, GalleryBlock,
  SignupBlock, BookingBlock, DonationsBlock, BannerBlock, FormBlock,
  CustomTextBlock, SingleImageBlock, EmbedBlock, ProductsBlock, SpacerBlock,
} from '../../blocks/CanvasBlocks';
import { renderPremiumSection } from '../../premium/PremiumSectionRenderer';
import { renderExt, EXT_BLOCK_TYPES } from '../../premium/PremiumSectionRendererExt';

// No-op inline editor — renders the value in the given tag/class.
// Iter 189c Phase B — matches the editor's `InlineText` behaviour by using
// `dangerouslySetInnerHTML` when the value contains HTML tags (e.g. `<em>`
// emphasis in mission-focus titles). Without this, template titles that
// ship rich text would render literal `<em>` characters in the preview.
const NoopE = ({ value, className, tag = 'span', placeholder }) => {
  const Tag = tag;
  const content = (value == null || value === '') ? (placeholder || '') : value;
  const isHtml = typeof content === 'string' && /<[a-z][\s\S]*>/i.test(content);
  if (isHtml) {
    return <Tag className={className} dangerouslySetInnerHTML={{ __html: content }} />;
  }
  return <Tag className={className}>{content}</Tag>;
};
const noop = () => {};

// Stubs so we can mount editor-native block components outside the editor.
// Renders visuals only — no toolbar chrome, no upload zones.
const StubToolbar = () => null;
const StubFullBlock = ({ children }) => <section>{children}</section>;
const StubBgLayer = ({ defaultBg = '', hasPadding = false, children }) => (
  <div className={`${defaultBg} ${hasPadding ? 'py-16 px-6' : ''}`}>{children}</div>
);

// Default palette + font used when the preview renders in isolation
// (outside the editor where the real theme comes from `useContentEditor`).
const DEFAULT_PREVIEW_PALETTE = ['#8B5CF6', '#3B82F6', '#0E0E23', '#FAF6EE'];
const DEFAULT_HEADING_FONT = 'Syne';

// Standard stub kit for editor-native block components. They all share the
// same "render with edit chrome" pattern; passing null-ish props renders
// their read-only visual.
const canvasBlockStubs = {
  E: NoopE, Toolbar: StubToolbar, FullBlock: StubFullBlock, BgLayer: StubBgLayer,
  onEdit: noop, onMediaPick: noop,
};

// Live renderers we support. Categories NOT listed here gracefully fall back
// to the drawn miniature in RichTilePreview.
const LIVE_RENDERERS = {
  hero: ({ block }) => (
    <HeroBlock
      block={block}
      content={block.content}
      onEdit={noop}
      onMediaPick={noop}
      fieldStyles={{}}
      E={NoopE}
    />
  ),
  about: ({ block }) => (
    <AboutBlock
      block={block}
      content={block.content}
      style={block.style || {}}
      {...canvasBlockStubs}
      imgSizeCls=""
      isImageTop={false}
      isImageBottom={false}
      isOverlay={false}
    />
  ),
  // Iter 189c Phase B · Testimonials — add live for `testimonial` (singular)
  // + marquee + all other CanvasBlocks exports. ErrorBoundary catches crashes.
  testimonial: ({ block }) => (
    <TestimonialBlock block={block} content={block.content} style={block.style || {}} {...canvasBlockStubs} />
  ),
  marquee:     ({ block }) => <MarqueeBlock     block={block} content={block.content} {...canvasBlockStubs} />,
  video:       ({ block }) => <VideoBlock       block={block} content={block.content} {...canvasBlockStubs} />,
  gallery:     ({ block }) => <GalleryBlock     block={block} content={block.content} style={block.style || {}} {...canvasBlockStubs} />,
  signup:      ({ block }) => <SignupBlock      block={block} content={block.content} style={block.style || {}} {...canvasBlockStubs} />,
  booking:     ({ block }) => <BookingBlock     block={block} content={block.content} style={block.style || {}} {...canvasBlockStubs} />,
  donations:   ({ block }) => <DonationsBlock   block={block} content={block.content} style={block.style || {}} {...canvasBlockStubs} />,
  banner:      ({ block }) => <BannerBlock      block={block} content={block.content} {...canvasBlockStubs} bgColor={undefined} bgImage={undefined} blockStyle={{}} />,
  form:        ({ block }) => <FormBlock        block={block} content={block.content} {...canvasBlockStubs} />,
  text:        ({ block }) => <CustomTextBlock  block={block} content={block.content} style={block.style || {}} {...canvasBlockStubs} />,
  image:       ({ block }) => <SingleImageBlock block={block} content={block.content} style={block.style || {}} {...canvasBlockStubs} imgSizeCls="" />,
  single_image: ({ block }) => <SingleImageBlock block={block} content={block.content} style={block.style || {}} {...canvasBlockStubs} imgSizeCls="" />,
  embed:       ({ block }) => <EmbedBlock       block={block} content={block.content} {...canvasBlockStubs} />,
  products:    ({ block }) => <ProductsBlock    block={block} content={block.content} style={block.style || {}} {...canvasBlockStubs} allProducts={[]} />,
  spacer:      ({ block }) => <SpacerBlock      block={block} content={block.content} Toolbar={StubToolbar} FullBlock={StubFullBlock} />,
};

// Premium-family types that render via renderPremiumSection().
const PREMIUM_LIVE_TYPES = new Set([
  'editorial', 'features', 'split', 'stats', 'logos', 'team', 'services',
  'directory', 'testimonials', 'timeline', 'events', 'cta',
]);

function pickLiveRenderer(block) {
  if (!block?.type) return null;
  if (LIVE_RENDERERS[block.type]) return () => LIVE_RENDERERS[block.type]({ block });
  const theme = {
    bg: '#ffffff', ink: '#0E0E23', accent: DEFAULT_PREVIEW_PALETTE[0],
    radius: 12, headingFont: DEFAULT_HEADING_FONT,
  };
  // Iter 189c Phase B — for premium-family types, try `renderExt` first so
  // layout-extension variants (e.g. `client-logos-wall`, `wall-of-love`,
  // `pricing-with-testimonial`) render via their ext renderer. If ext
  // returns null (layout not registered as ext-owned), fall through to
  // the base renderPremiumSection.
  if (PREMIUM_LIVE_TYPES.has(block.type)) {
    return () => {
      const ext = renderExt(block, theme, noop, noop);
      if (ext) return ext;
      return renderPremiumSection(
        block, DEFAULT_PREVIEW_PALETTE, DEFAULT_HEADING_FONT, 12, noop, noop,
      );
    };
  }
  // Ext-only types (product_grid, product_detail, cart_table, faq, contact,
  // footer, agency…) render via renderExt.
  if (EXT_BLOCK_TYPES.has(block.type)) {
    return () => renderExt(block, theme, noop, noop);
  }
  return null;
}

/**
 * Error boundary — any render crash inside the preview falls silently to
 * `null` so the parent can substitute the drawn miniature.
 */
class PreviewBoundary extends React.Component {
  constructor(props) { super(props); this.state = { crashed: false }; }
  static getDerivedStateFromError() { return { crashed: true }; }
  componentDidCatch(err) {
    if (typeof window !== 'undefined' && window.__wb_preview_debug) console.warn('LiveSectionPreview crashed:', err);
  }
  render() {
    if (this.state.crashed) return null;
    return this.props.children;
  }
}

export default function LiveSectionPreview({ tpl }) {
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(0.32);

  useEffect(() => {
    const compute = () => {
      const el = wrapRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const s = Math.max(0.15, Math.min(0.6, (rect.width - 8) / 1200));
      setScale(s);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);

  const block = tpl?.blocks?.[0];
  if (!block) return null;
  const renderer = pickLiveRenderer(block);
  if (!renderer) return null;

  return (
    <div
      ref={wrapRef}
      className="w-full h-full overflow-hidden relative bg-white"
      data-testid="live-section-preview"
    >
      <div
        style={{
          width: 1200,
          minHeight: 720,
          transformOrigin: 'top left',
          transform: `scale(${scale})`,
          pointerEvents: 'none',
        }}
      >
        <PreviewBoundary>
          {renderer()}
        </PreviewBoundary>
      </div>
    </div>
  );
}

/**
 * Whether this template can be rendered with a live (drift-free) renderer.
 * Callers use this to decide which badge / preview to show.
 */
export const hasLiveRenderer = (tpl) => {
  const block = tpl?.blocks?.[0];
  if (!block) return false;
  return Boolean(pickLiveRenderer(block));
};
